import { useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Reveal } from "@/components/Reveal";
import { BookCallButton } from "@/components/Ctas";
import LeadForm from "@/components/LeadForm";
import InquiryForm from "@/components/InquiryForm";

const TYPED = {
  "strategy-sprint": {
    eyebrow: "Strategy Sprint",
    title: "Let's figure out what to do next.",
    copy: "Tell us a little about what you're navigating. The Strategy Sprint is designed to help you understand what's happening, identify what matters most and build a practical path forward.",
    mode: "sprint",
    formTestid: "sprint-inquiry-form",
  },
  workshop: {
    eyebrow: "Workshop",
    title: "Let's build the right workshop.",
    copy: "Tell us a little about your team, your challenge and what you'd like to build capability around. We'll help shape a workshop around your context, goals and way of working.",
    mode: "workshop",
    formTestid: "workshop-inquiry-form",
  },
};

export default function Contact() {
  const [params] = useSearchParams();
  const typed = TYPED[params.get("type")] || null;

  useEffect(() => {
    document.title = typed
      ? `${typed.eyebrow} Inquiry — KP Strategy`
      : "Get in Touch — KP Strategy";
    return () => {
      document.title =
        "KP Strategy — Design your business like a product. Run it like a system. Scale it with AI.";
    };
  }, [typed]);

  return (
    <main data-testid="contact-page">
      <section className="pt-[72px]">
        <div className="mx-auto max-w-7xl px-4 pb-24 pt-16 sm:px-6 sm:pt-24 lg:px-8 lg:pt-28">
          <div className="grid grid-cols-1 gap-14 lg:grid-cols-12 lg:gap-16">
            <div className="lg:col-span-5">
              <Reveal>
                <p className="flex items-center gap-3 font-mono text-xs font-medium uppercase tracking-[0.3em] text-amber">
                  <span className="inline-block h-px w-10 bg-amber" aria-hidden="true" />
                  {typed ? typed.eyebrow : "Contact"}
                </p>
              </Reveal>
              <Reveal delay={0.08}>
                <h1 className="mt-7 font-serif text-4xl font-light leading-[1.08] tracking-tight text-ink sm:text-5xl md:text-6xl">
                  {typed ? typed.title : "Get in touch."}
                </h1>
              </Reveal>
              <Reveal delay={0.16}>
                <p className="mt-7 max-w-md text-base leading-relaxed text-body sm:text-lg">
                  {typed
                    ? typed.copy
                    : "Tell me a little about you and what you're working on. A sentence or two is plenty — we'll take it from there."}
                </p>
              </Reveal>
              <Reveal delay={0.24}>
                <div className="mt-10 border-t border-line pt-8">
                  <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-faint">
                    Prefer to talk first?
                  </p>
                  <div className="mt-4">
                    <BookCallButton testid="contact-cta-book-call" />
                  </div>
                </div>
              </Reveal>
            </div>
            <div className="lg:col-span-7">
              <Reveal delay={0.2}>
                <div className="border border-line bg-parch p-8 sm:p-10">
                  {typed ? (
                    <InquiryForm mode={typed.mode} testid={typed.formTestid} />
                  ) : (
                    <LeadForm
                      source="contact"
                      submitLabel="Send Message"
                      testid="contact-form"
                    />
                  )}
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
